import {useEffect, useMemo} from 'react';
import useData from 'utils/useData';
import {useStoreActions, useStoreState} from 'store';
import {Storyboard} from 'entities/storyboard';
import {StoryboardsModel} from 'views/storyboard/stores/storyboard';
import useOptimizedConfig from '@/hooks/useOptimizedConfig';
import {buildURL} from '@/utils/urlBuilder';


const useStoryboardsData = (): [Storyboard[], boolean, ((_: any) => any), Error | null] => {
    const activeStoryboardHashid = useStoreState((state) => state.activeStoryboard.activeHashid);
    const activeStoryUrl = useStoreState((state) => state.activeDummyStory.activeDummyStorySlug);
    const activeStoryboardUrl = useStoreState((state) => state.activeDummyStoryboard.activeDummyStoryboardSlug);


    const setStoryboardsReload = useStoreActions((actions) => actions.storyboards.setReload);
    const {config, loading: configLoading} = useOptimizedConfig();
    const urls = config?.URLS || {};
    const urlsLoaded = !configLoading && !!config;
    

    const {storyboardUrl, requestParams} = useMemo(() => {
        let url: string = '';
        let params = {};
        
        if (activeStoryboardHashid) {
            url = urls.storyboards || '';
            params = {
                params: {storyboardHashid: activeStoryboardHashid},
            };
        } else if (activeStoryUrl) {
            if (urlsLoaded) {
                url = buildURL('dummyStoryboardsResource', {
                    story_assigned_url: activeStoryUrl,
                    storyboard_assigned_url: activeStoryboardUrl || 'fake'
                });
            }
        }
        
        return {storyboardUrl: url, requestParams: params};
    }, [activeStoryboardHashid, activeStoryUrl, activeStoryboardUrl, urls.storyboards, urlsLoaded]);
    

    const [storyboards, loadingStoryboards, storeAction, error] = useData<Storyboard[], StoryboardsModel>(
        storyboardUrl, {
            storeResolver: 'storyboards',
            actionResolver: 'setStoryboards',
            loadingResolver: 'loading',
            setLoadingResolver: 'getLoadingLock',
            reloadOnParamsChange: false,
            useCachedData: true,
            requestParams,
        }
    );
    

    const availableStoryboardHashids = useMemo(() => {
        return Array.isArray(storyboards) ? storyboards.map((x) => x.hashid) : [];
    }, [storyboards]);

    useEffect(() => {
        // Explicit hook to reload storyboards data when a story changes. Otherwise the activeStoryboard
        // will be set to something that is not available in Redux.storyboards state and a blank page is shown
        // If there was an error loading storyboards (e.g. 403 Forbidden),
        // do not trigger infinite reload loops.
        if (error) {
            return;
        }

        if (
            activeStoryboardHashid
            && !loadingStoryboards
            && !availableStoryboardHashids.includes(activeStoryboardHashid)
        ) {
            setStoryboardsReload(true);
        }
    }, [
        loadingStoryboards,
        availableStoryboardHashids,
        activeStoryboardHashid,
        setStoryboardsReload,
        error,
    ]);

    return [storyboards, loadingStoryboards, storeAction, error];
};

export default useStoryboardsData;
